import {
  ArrowLeft,
  ArrowRight,
  BookOpenCheck,
  Bookmark,
  Check,
  Clock3,
  FileQuestion,
  GraduationCap,
  LayoutDashboard,
  Layers3,
  LockKeyhole,
  MessageCircle,
  Search,
  Send,
  Sparkles,
  Target,
} from "lucide-react";

const NAV_ITEMS = [
  { id: "dashboard", label: "Dashboard", icon: LayoutDashboard },
  { id: "learn", label: "Learn", icon: GraduationCap },
  { id: "question", label: "Question Bank", icon: BookOpenCheck },
  { id: "sprint", label: "Exam Mode", icon: FileQuestion },
  { id: "word-bank", label: "Word Bank", icon: Layers3 },
];

const SCREEN_LABELS = {
  learn: "Learning path",
  dashboard: "Study dashboard",
  question: "Illinois state practice",
  explanation: "Answer review",
  score: "Progress and readiness",
  "word-bank": "Word Bank",
  sprint: "Timed Review Sprint",
};

const ACTIVE_NAV = {
  explanation: "question",
  score: "dashboard",
};

const Metric = ({ label, value, accent }) => (
  <div className={`ibp-preview-metric${accent ? " is-accent" : ""}`}>
    <span>{label}</span>
    <strong>{value}</strong>
  </div>
);

const ProgressBar = ({ value, tone = "gold" }) => (
  <span className="ibp-preview-progress" aria-hidden="true">
    <span className={`is-${tone}`} style={{ width: `${value}%` }} />
  </span>
);

const ScreenHeading = ({ eyebrow, title, detail, aside }) => (
  <div className="ibp-preview-heading">
    <div className="ibp-preview-heading__copy">
      {eyebrow ? <span className="ibp-preview-eyebrow">{eyebrow}</span> : null}
      <h3>{title}</h3>
      {detail ? <p>{detail}</p> : null}
    </div>
    {aside}
  </div>
);

const DashboardScreen = () => (
  <>
    <ScreenHeading
      eyebrow="Your study home"
      title="Welcome back, Jordan"
      detail="Pick up where you left off and keep building exam readiness."
      aside={<span className="ibp-preview-date">12 days to exam</span>}
    />
    <div className="ibp-preview-metrics">
      <Metric label="Readiness" value="72%" accent />
      <Metric label="Coverage" value="64%" />
      <Metric label="Questions" value="148" />
    </div>
    <div className="ibp-preview-dashboard-grid">
      <section className="ibp-preview-panel ibp-preview-next-session">
        <span className="ibp-preview-kicker">Recommended next session</span>
        <h4>Finish Illinois licensing, then run its checkpoint.</h4>
        <div className="ibp-preview-lesson-row">
          <span>Lesson 3 of 8</span>
          <strong>68%</strong>
        </div>
        <ProgressBar value={68} />
        <span className="ibp-preview-primary-action">
          Continue lesson <ArrowRight />
        </span>
      </section>
      <section className="ibp-preview-panel">
        <span className="ibp-preview-kicker">Weak areas</span>
        <div className="ibp-preview-topic-row">
          <span>Escrow accounts</span><strong>58%</strong>
        </div>
        <ProgressBar value={58} tone="red" />
        <div className="ibp-preview-topic-row">
          <span>Agency duties</span><strong>66%</strong>
        </div>
        <ProgressBar value={66} />
      </section>
    </div>
  </>
);

const LearnScreen = () => (
  <>
    <ScreenHeading
      eyebrow="Illinois learning path"
      title="Learn the rule before you drill it."
      detail="Short lessons connect plain-English guidance to the source and a quick checkpoint."
      aside={<span className="ibp-preview-date">Lesson 3 of 8</span>}
    />
    <div className="ibp-preview-learn-grid">
      <section className="ibp-preview-panel ibp-preview-lesson-card">
        <span className="ibp-preview-kicker">Illinois licensing</span>
        <h4>“Broker” is the entry-level license in Illinois.</h4>
        <p>
          Illinois no longer issues a salesperson license. A new licensee who completes the
          required education and passes the exam applies as a Broker.
        </p>
        <div className="ibp-preview-source">
          <LockKeyhole /> Source: 225 ILCS 454/5-27
        </div>
        <div className="ibp-preview-key-terms">
          <span>Broker</span><span>Sponsoring broker</span><span>Managing broker</span>
        </div>
      </section>
      <section className="ibp-preview-panel ibp-preview-checkpoint">
        <span className="ibp-preview-kicker">Lesson progress</span>
        <strong className="ibp-preview-large-number">3 / 8</strong>
        <ProgressBar value={38} />
        <p>One short checkpoint unlocks after this lesson.</p>
        <span className="ibp-preview-primary-action">
          Continue <ArrowRight />
        </span>
      </section>
    </div>
  </>
);

const AnswerOption = ({ letter, children, selected, correct }) => (
  <div className={`ibp-preview-answer${selected ? " is-selected" : ""}${correct ? " is-correct" : ""}`}>
    <span>{letter}</span>
    <p>{children}</p>
    {correct ? <Check /> : null}
  </div>
);

const QuestionScreen = () => (
  <>
    <ScreenHeading
      eyebrow="Illinois state law practice"
      title="Question 12 of 25"
      detail="Escrow requirements"
      aside={<span className="ibp-preview-date">Medium</span>}
    />
    <section className="ibp-preview-panel ibp-preview-question-card">
      <h4>Where must a sponsoring broker’s escrow account be maintained?</h4>
      <div className="ibp-preview-answer-list">
        <AnswerOption letter="A">At any institution the broker prefers, insured or not</AnswerOption>
        <AnswerOption letter="B">Only at an Illinois-chartered bank</AnswerOption>
        <AnswerOption letter="C" selected>At a federally insured depository</AnswerOption>
        <AnswerOption letter="D">In an account at a securities brokerage</AnswerOption>
      </div>
      <div className="ibp-preview-question-actions">
        <span className="ibp-preview-secondary-action"><Bookmark /> Save for later</span>
        <span className="ibp-preview-primary-action">Submit answer <ArrowRight /></span>
      </div>
    </section>
  </>
);

export const QuestionCoachDemo = ({ compact = false, className = "" }) => (
  <div className={`ibp-coach-demo${compact ? " is-compact" : ""} ${className}`.trim()}>
    <div className="ibp-coach-demo__header">
      <span className="ibp-coach-demo__identity">
        <span className="ibp-coach-demo__icon"><Sparkles /></span>
        <span><strong>Question Coach</strong><small>Grounded in Q42 and its source</small></span>
      </span>
      <span className="ibp-coach-demo__status">AI enhanced</span>
    </div>
    <div className="ibp-coach-demo__context">
      <MessageCircle /> Escrow requirements · Answer C
    </div>
    <div className="ibp-coach-demo__messages">
      <div className="ibp-coach-demo__user">Why is B too narrow?</div>
      <div className="ibp-coach-demo__reply">
        {compact ? (
          <>The rule requires a <strong>federally insured depository</strong>. B adds an Illinois-chartered restriction the rule does not contain.</>
        ) : (
          <>The rule requires a <strong>federally insured depository</strong>. It does not require the institution to be Illinois-chartered, so B adds a restriction the rule does not contain.</>
        )}
      </div>
    </div>
    <div className="ibp-coach-demo__suggestions">
      <span>Explain more simply</span><span>Compare all choices</span>
    </div>
    <div className="ibp-coach-demo__composer">
      <span>Ask about this answer…</span><span className="ibp-coach-demo__send"><Send /></span>
    </div>
  </div>
);

const ExplanationScreen = () => (
  <>
    <ScreenHeading
      eyebrow="Answer review"
      title="Correct. Now make the rule stick."
      detail="Review the reasoning, the traps, and the source before moving on."
      aside={<span className="ibp-preview-correct"><Check /> Correct</span>}
    />
    <div className="ibp-preview-explanation-grid">
      <section className="ibp-preview-panel ibp-preview-rationale">
        <span className="ibp-preview-kicker">Why C is correct</span>
        <h4>Federal insurance is the requirement.</h4>
        <p>
          Illinois escrow accounts must be held at a federally insured depository; the rule
          does not require an Illinois-chartered bank.
        </p>
        <div className="ibp-preview-trap">
          <strong>Why B is wrong</strong>
          <span>It is narrower than the actual rule.</span>
        </div>
        <div className="ibp-preview-source">
          <LockKeyhole /> 68 Ill. Adm. Code 1450.750(i)(4)
        </div>
      </section>
      <QuestionCoachDemo compact />
    </div>
  </>
);

const ScoreScreen = () => (
  <>
    <ScreenHeading
      eyebrow="Progress and readiness"
      title="Your practice is turning into a plan."
      detail="Readiness combines accuracy with how much of the active library you have covered."
      aside={<span className="ibp-preview-date">Updated today</span>}
    />
    <div className="ibp-preview-score-grid">
      <section className="ibp-preview-panel ibp-preview-readiness-card">
        <span className="ibp-preview-kicker">Exam readiness</span>
        <strong className="ibp-preview-readiness-score">72%</strong>
        <ProgressBar value={72} />
        <p>Keep building coverage while holding accuracy above 80%.</p>
      </section>
      <section className="ibp-preview-panel ibp-preview-section-scores">
        <span className="ibp-preview-kicker">Section performance</span>
        <div className="ibp-preview-topic-row"><span>Illinois</span><strong>84%</strong></div>
        <ProgressBar value={84} tone="green" />
        <div className="ibp-preview-topic-row"><span>National</span><strong>76%</strong></div>
        <ProgressBar value={76} />
        <div className="ibp-preview-topic-row"><span>Math</span><strong>69%</strong></div>
        <ProgressBar value={69} tone="red" />
      </section>
      <section className="ibp-preview-panel ibp-preview-recommendation">
        <Target />
        <div><span className="ibp-preview-kicker">Next best move</span><h4>Drill 10 math questions</h4></div>
        <ArrowRight />
      </section>
    </div>
  </>
);

const WordBankScreen = () => (
  <>
    <ScreenHeading
      eyebrow="Vocabulary and concepts"
      title="Word Bank"
      detail="Review a focused deck built from the concepts in your study library."
      aside={<span className="ibp-preview-date">Card 18 of 64</span>}
    />
    <div className="ibp-preview-search"><Search /> Search terms, topics, or Illinois rules</div>
    <div className="ibp-preview-word-grid">
      <section className="ibp-preview-flashcard">
        <div className="ibp-preview-flashcard__top">
          <span>Illinois · Licensing</span><Bookmark />
        </div>
        <h4>Sponsoring broker</h4>
        <p>
          A managing broker or brokerage entity responsible for supervising the licensed
          activities of an Illinois Broker.
        </p>
        <span className="ibp-preview-source"><LockKeyhole /> Reviewed concept</span>
      </section>
      <section className="ibp-preview-panel ibp-preview-deck-progress">
        <span className="ibp-preview-kicker">Today’s deck</span>
        <strong className="ibp-preview-large-number">18 / 25</strong>
        <ProgressBar value={72} />
        <div><span>Know it</span><strong>12</strong></div>
        <div><span>Review again</span><strong>6</strong></div>
      </section>
    </div>
    <div className="ibp-preview-deck-actions">
      <span className="ibp-preview-secondary-action"><ArrowLeft /> Previous</span>
      <span className="ibp-preview-primary-action">Next card <ArrowRight /></span>
    </div>
  </>
);

const SprintScreen = () => (
  <>
    <ScreenHeading
      eyebrow="Timed Review Sprint"
      title="Illinois disclosures"
      detail="Question 8 of 20 · 6 answered"
      aside={<span className="ibp-preview-timer"><Clock3 /> 12:44</span>}
    />
    <div className="ibp-preview-palette" aria-hidden="true">
      {[1, 2, 3, 4, 5, 6, 7, 8].map((item) => (
        <span key={item} className={item === 8 ? "is-current" : item < 7 ? "is-answered" : ""}>{item}</span>
      ))}
      <em>12 remaining</em>
    </div>
    <section className="ibp-preview-panel ibp-preview-sprint-card">
      <span className="ibp-preview-kicker">Seller disclosure</span>
      <h4>
        A buyer receives the seller’s disclosure report after signing the contract. What should
        the buyer review first?
      </h4>
      <div className="ibp-preview-answer-list is-compact">
        <AnswerOption letter="A" selected>Whether material defects are disclosed</AnswerOption>
        <AnswerOption letter="B">The buyer’s loan amortization schedule</AnswerOption>
        <AnswerOption letter="C">The sponsoring broker’s advertising budget</AnswerOption>
      </div>
      <div className="ibp-preview-question-actions">
        <span className="ibp-preview-secondary-action"><Bookmark /> Flag question</span>
        <span className="ibp-preview-primary-action">Save and continue <ArrowRight /></span>
      </div>
    </section>
  </>
);

const SCREENS = {
  dashboard: DashboardScreen,
  learn: LearnScreen,
  question: QuestionScreen,
  explanation: ExplanationScreen,
  score: ScoreScreen,
  "word-bank": WordBankScreen,
  sprint: SprintScreen,
};

export const ProductPreview = ({ screen = "dashboard", compact = false, className = "" }) => {
  const Screen = SCREENS[screen] || DashboardScreen;
  const active = ACTIVE_NAV[screen] || screen;
  const label = SCREEN_LABELS[screen] || SCREEN_LABELS.dashboard;

  return (
    <div
      className={`ibp-product-preview${compact ? " is-compact" : ""} ${className}`.trim()}
      role="img"
      aria-label={`${label} interface preview from Illinois Broker Prep`}
    >
      <div className="ibp-product-preview__browser">
        <span className="ibp-product-preview__dots"><i /><i /><i /></span>
        <span className="ibp-product-preview__url"><LockKeyhole /> illinoisbrokerprep.com/study</span>
        <span className="ibp-product-preview__live">Student access</span>
      </div>
      <div className="ibp-product-preview__app">
        <aside className="ibp-product-preview__sidebar">
          <div className="ibp-product-preview__brand">
            <span>IBP</span>
            <strong>Illinois Broker Prep</strong>
          </div>
          <nav>
            {NAV_ITEMS.map(({ id, label: navLabel, icon: Icon }) => (
              <span key={id} className={active === id ? "is-active" : ""}>
                <Icon /> <em>{navLabel}</em>
              </span>
            ))}
          </nav>
          <span className="ibp-product-preview__support"><MessageCircle /> Support</span>
        </aside>
        <section className="ibp-product-preview__workspace">
          <header>
            <span>{label}</span>
            <span className="ibp-product-preview__student">JM</span>
          </header>
          <main><Screen /></main>
        </section>
      </div>
    </div>
  );
};

export default ProductPreview;
